import { HttpErrorResponse } from '@angular/common/http';
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { Activity, WorkloadApiService } from '@horizon/core';
import { ToastService } from '@horizon/core-notifications';
import { TranslateService } from '@ngx-translate/core';
import { GenesysData, Row, SearchData } from '@shared/interfaces/CallLog';
import { PaymentNotification } from '@shared/interfaces/PaymentNotifications';
import { ApiExceptionsHandlerService } from '@shared/services/api-exceptions-handler.service';
import { HorizonPaymentGatewayService } from '@shared/services/horizon-payment-gateway.service';
import { StorageApiService } from '@shared/services/storage-api.service';
import { BeforeCloseEventArgs } from '@syncfusion/ej2-angular-popups';
import {
  catchError,
  filter,
  finalize,
  Observable,
  shareReplay,
  Subject,
  takeUntil,
  tap,
} from 'rxjs';
import { HorizonGenesysCallLogService } from '../../services/horizon-genesys-call-log.service';
import { PaymentSocketData } from '../horizon-payment-getaway/models/horizon-payment-getaway.model';
import {
  Content,
  OpenCallLogReportEmitter,
  ReportData,
  ReportDataVM,
} from './models/';
import { FormsService } from './services/forms.service';
import { PaymentNotificationsService } from './services/payment-notifications.service';
import { CallLogReportViewComponent } from './views/call-log-report-view/call-log-report-view.component';
import { CallSearchViewComponent } from './views/call-log-search-view/call-log-search-view.component';

@Component({
  selector: 'horizon-genesys-call-log',
  templateUrl: './horizon-genesys-call-log.component.html',
  styleUrls: ['./horizon-genesys-call-log.component.scss'],
})
export class HorizonGenesysCallLogComponent implements OnInit, OnDestroy {
  @Input() activityId!: number;
  @Input() callLogOrigin:
    | 'Default'
    | 'CustomerOverview'
    | 'ViewCallLog'
    | 'Report';

  @Input() isEditMode: boolean;
  @Output() closeEvent = new EventEmitter<BeforeCloseEventArgs>();
  @ViewChild('CallLogSearch') callLogSearch: CallSearchViewComponent;
  @ViewChild('CallLogReport') callLogReport: CallLogReportViewComponent;

  private readonly destroy$ = new Subject<void>();
  private readonly beforeUnloadEventListener: (
    event: BeforeUnloadEvent
  ) => void;
  genesysData: GenesysData;
  accountId: number;
  callId: string;
  callSubType: string;
  callType: string;
  customerId: number;
  formData: SearchData;
  isPCIPalOpen = false;
  paymentData: object;
  paymentProcessed = false;
  paymentRequirementNotifications: Array<object>;
  paymentNotification: PaymentNotification;
  reportData: ReportDataVM;
  reminders$: Observable<Activity[]>;

  title: string;
  callLogView: 'Search' | 'Report' | 'PCIPal' = 'Search';

  constructor(
    public translate: TranslateService,
    private readonly apiExceptionsHandlerService: ApiExceptionsHandlerService,
    private readonly callLogService: HorizonGenesysCallLogService,
    private readonly formsService: FormsService,
    private readonly paymentGatewayService: HorizonPaymentGatewayService,
    private readonly paymentNotificationsService: PaymentNotificationsService,
    private readonly storageApiService: StorageApiService,
    private readonly toastService: ToastService,
    private readonly workloadApi: WorkloadApiService
  ) {}

  ngOnInit(): void {
    if (this.callLogOrigin === 'ViewCallLog' && !this.isEditMode) {
      this.title = 'call_log.titles.call_log_report_view';
      this.initializeViewCallLog();
    } else if (this.callLogOrigin === 'CustomerOverview' && this.isEditMode) {
      this.getGenesysData();
      this.title = 'call_log.titles.call_log_report_view';
      this.initializeCustomerOverviewReport();
    } else {
      this.getGenesysData();
      this.title = 'call_log.titles.call_log_search_view';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(undefined);
    this.destroy$.complete();
    this.callLogService.clearAllData();
    window.removeEventListener('beforeunload', this.beforeUnloadEventListener);
  }

  cancelEvent(event: boolean): void {
    if (event) {
      this.close();
      window.close();
    }
  }

  beforeClose(args: BeforeCloseEventArgs) {
    this.storageApiService.deleteLocalStorageData('genesysData');
    this.storageApiService.deleteLocalStorageData('thirdPartyCall');
    this.storageApiService.deleteLocalStorageData('thirdPartyCalledData');
    this.storageApiService.deleteLocalStorageData('processed');
    this.storageApiService.deleteLocalStorageData('isViewCustomerOverview');
    this.storageApiService.deleteLocalStorageData('isCustomerOverview');
  }

  close(args?: BeforeCloseEventArgs): void {
    this.closeEvent.emit(args);
    setTimeout(() => {
      window.close();
    }, 2000);
  }

  onOpen(args: any): void {
    args.preventFocus = true;
  }

  public openPCIPal(): void {
    this.paymentGatewayService
      .GeneratePCIPalSession({
        linkToCall: this.genesysData.callID,
        accountId: this.accountId,
        customerId: this.customerId,
      })
      .pipe(
        takeUntil(this.destroy$),
        filter((session) => !!session),
        tap((session) => {
          this.paymentData = session;
          this.isPCIPalOpen = true;
          this.reportData.callLog.sessionId = session.sessionId;
        }),
        catchError((error) => {
          this.isPCIPalOpen = false;
          return this.apiExceptionsHandlerService.catchErrorHandler(
            error,
            'payment-gateway'
          );
        }),
        finalize(() => this.callLogReport.paymentButton.stopLoading())
      )
      .subscribe();
  }

  public paymentFinished(event: PaymentSocketData) {
    this.isPCIPalOpen = false;
    if (event && event.paymentStatus) {
      this.paymentNotification =
        this.paymentNotificationsService.controlPaymentResponse(event, true);
      if (this.paymentNotification.status === 'success') {
        this.paymentProcessed = true;
      }
    }
  }

  public saveReportData(reportData: ReportData): void {
    this.workloadApi
      .saveCallLogActivity(reportData)
      .pipe(
        takeUntil(this.destroy$),
        tap({
          next: () =>
            this.toastService.showToastNotification({
              text: 'Call Log saved',
              type: 'success',
            }),
          complete: () => this.close(),
        }),
        catchError((error) =>
          this.apiExceptionsHandlerService.catchErrorHandler(
            error,
            'Workload API'
          )
        ),
        finalize(() => this.callLogReport.confirmationButtons.onStopLoading())
      )
      .subscribe();
  }

  public swapCallLogView(event: OpenCallLogReportEmitter): void {
    const { callType, callSubtype, accountId, customerId, relationship } =
      event;
    this.callType = callType;
    this.callSubType = callSubtype;
    this.accountId = accountId;
    this.customerId = customerId;

    this.callLogService
      .getCallLogReport(
        customerId,
        accountId,
        callType,
        callSubtype,
        relationship
      )
      .pipe(
        takeUntil(this.destroy$),
        filter((reportData) => this.formsService.filterSearchData(reportData)),
        tap({
          next: (reportData) => {
            this.formData = { ...reportData };
            this.buildPaymentAvailabilityNotifications(
              this.formData.General.data.rows[0]
            );
            this.storageApiService.setLocalStorageData(
              'isViewCustomerOverview',
              'true'
            );
            this.toastService.showToastNotification({
              text: 'call_log.success.found_data',
              type: 'success',
            });
            this.swapCallLogViewCompleted();
          },
          complete: () => {
            window.open(`/account/${accountId}/customers-overview`, '_blank');
          },
        }),
        catchError((error) =>
          this.apiExceptionsHandlerService.catchErrorHandler(error, 'Call Log')
        ),
        finalize(() => this.callLogSearch.openCallLogButton.stopLoading())
      )
      .subscribe();
  }

  private buildReportData(): void {
    try {
      const generalData = this.formData['General'].data.rows[0];
      const {
        scopeId,
        entityId,
        category,
        id,
        idvStatus,
        successfulContact,
        customerId,
        vulnerability,
        relationship,
        callSummary,
        callSummaryEdited,
        callClosure,
      } = generalData;

      this.reportData = new ReportDataVM({
        scopeId: scopeId?.value ?? -1,
        entityId: entityId?.value?.Id || entityId?.value?.accountId,
        category: category?.value,
        subEntityId: customerId?.value,
        activityText: '',
        entityType: '',
        callLog: {
          id: id?.value ?? -1,
          type: this.callType,
          subtype: this.callSubType,
          idVStatus: idvStatus?.value ?? 'Uncomplete',
          successfulContact: successfulContact?.value,
          customerId: customerId?.value,
          linkedAccounts: this.getLinkedAccountsIds(),
          additionalFields: {
            Vulnerability: vulnerability?.value,
            Relationship: relationship?.value,
            LinkedAccounts: this.pregenerateLinkedAccounts(),
          },
          content: [],
          sessionId: null,
          telephoneNote: '',
          callSummary: callSummary?.value ?? '',
          callSummaryEdited: callSummaryEdited?.value ?? '',
          callClosure: callClosure.value,
          callId: this.genesysData?.callID || '',
          phoneNumber: this.genesysData?.phoneNumber || '',
        },
        tasksToClose: [],
      });

      this.callLogService.setReportData(this.reportData);

      const contentData = this.pregenerateReportData();
      this.reportData.callLog.content = contentData;
      this.callLogService.setReportData(this.reportData);
    } catch (error) {
      this.toastService.showToastNotification({
        text: 'call_log.errors.initialized_call_log',
        type: 'error',
      });
      console.error('Error building report data:', error.message);
    }
  }

  private buildPaymentDataNotification(generalRowData: Row) {
    const paymentData =
      this.paymentNotificationsService.buildEditPaymentNotification(
        generalRowData
      );
    if (paymentData && paymentData.paymentStatus) {
      this.paymentNotification =
        this.paymentNotificationsService.controlPaymentResponse(
          paymentData,
          false
        );
    }
  }

  private buildPaymentAvailabilityNotifications(generalRowData: Row) {
    if (
      generalRowData.paymentAvailability &&
      !generalRowData.paymentAvailability.value &&
      generalRowData.paymentAvailability.extraData.length > 0
    ) {
      this.paymentRequirementNotifications =
        generalRowData.paymentAvailability.extraData;
    }
  }

  private getGenesysData(): void {
    this.genesysData = this.storageApiService.getLocalStorageData(
      'genesysData'
    ) as unknown as GenesysData;
  }

  private getLinkedAccountsIds(): Array<number> {
    let linkedAccounts = [];
    if (
      this.formData['General'].data.rows[0].linkedAccounts &&
      this.formData['General'].data.rows[0].linkedAccounts.value &&
      this.formData['General'].data.rows[0].linkedAccounts.value.length > 0
    ) {
      linkedAccounts = this.formData[
        'General'
      ].data.rows[0].linkedAccounts.value.map(
        (linkedAccount) => linkedAccount.accountId ?? linkedAccount.AccountId
      );
    }
    return linkedAccounts;
  }

  private initializeViewCallLog(): void {
    this.callLogService
      .getCallLogView(this.activityId)
      .pipe(
        takeUntil(this.destroy$),
        filter((res) => !!res),
        tap({
          next: (callLogView) => {
            this.formData = callLogView;
            this.buildPaymentDataNotification(callLogView.General.data.rows[0]);
            this.callLogService.setFormData(this.formData);
          },
          error: (error: HttpErrorResponse) =>
            this.apiExceptionsHandlerService.defaultErrorToasts(
              error,
              'Call Log API'
            ),
          complete: () => {
            this.toastService.showToastNotification({
              text: 'call_log.success.initialized_call_log',
              type: 'success',
            });
            this.callLogOrigin = 'Report';
            this.title = 'call_log.titles.call_log_report_view';
            this.buildReportData();
          },
        })
      )
      .subscribe();
  }

  private disableClientFromCallCampaigns(): void {
    const allRelatedAccount: Array<number> = [
      ...this.formData.General.data.rows[0].linkedAccounts?.value.map(
        (linkedAccount) =>
          linkedAccount.accountRef ?? Number(linkedAccount.accountRef)
      ),
      this.formData.General.data.rows[0].entityId.value?.accountRef ??
        Number(this.formData.General.data.rows[0].entityId.value?.accountRef),
    ];
    this.callLogService
      .disableClientFromCallCampaigns({
        phoneNumber: this.genesysData.phoneNumber,
        accounts: allRelatedAccount,
      })
      .pipe(
        takeUntil(this.destroy$),
        catchError((err) =>
          this.apiExceptionsHandlerService.catchErrorHandler(err, 'Genesys')
        )
      )
      .subscribe();
  }

  private initializeCustomerOverviewReport(): void {
    this.callType = 'Outbound';
    this.callSubType = 'Agent';
    this.customerId = Number(localStorage.getItem('currentCustomerId'));
    this.accountId = Number(localStorage.getItem('currentAccountId'));
    this.getCallLogReport();
    this.getReminders();
  }

  private getCallLogReport() {
    this.callLogService
      .getCallLogReport(
        this.customerId,
        this.accountId,
        this.callType,
        this.callSubType,
        localStorage.getItem('currentRelationship')
      )
      .pipe(
        takeUntil(this.destroy$),
        filter((res) => res !== undefined),
        tap({
          next: (callLogReport) => (this.formData = { ...callLogReport }),
          error: (error: HttpErrorResponse) =>
            this.apiExceptionsHandlerService.defaultErrorToasts(
              error,
              'Dashboard'
            ),
          complete: () => this.onGetCallLogReportComplete(),
        })
      )
      .subscribe();
  }

  private onGetCallLogReportComplete() {
    if (
      this.formData.General &&
      this.formData.General.data &&
      this.formData.ID_V_Prompt &&
      this.formData.ID_V_Prompt.data
    ) {
      this.toastService.showToastNotification({
        text: 'call_log.success.found_data',
        type: 'success',
      });
      this.buildPaymentAvailabilityNotifications(
        this.formData.General.data.rows[0]
      );
      this.swapCallLogViewCompleted();
    } else {
      this.toastService.showToastNotification({
        text: 'call_log.errors.found_data',
        type: 'error',
      });
    }
    this.storageApiService.setLocalStorageData('isCustomerOverview', 'false');
  }

  private pregenerateLinkedAccounts(): Array<any> {
    const linkedAccounts =
      this.formData['General'].data.rows[0].linkedAccounts.value;
    return linkedAccounts.map((linkedAccount) => {
      return {
        AccountId: linkedAccount.accountId ?? linkedAccount.AccountId,
        AccountRef: linkedAccount.accountRef ?? linkedAccount.AccountRef,
        Selected: linkedAccount.selected ?? linkedAccount.Selected,
      };
    });
  }

  private async waitForFormData(): Promise<void> {
    while (
      !!this.formData ||
      !!this.formData['ID_V_Prompt'] ||
      !!this.formData['ID_V_Prompt'].data
    ) {
      await new Promise((resolve) => setTimeout(resolve, 50));
    }
  }

  private pregenerateReportData(): Array<any> {
    const reportData: Array<Content> = this.formData['ID_V_Prompt'].data.rows
      .filter(
        (row) =>
          row.type.value === 'ID' ||
          row.type.value === 'Verification' ||
          row.type.value === 'ThirdParty'
      )
      .map((row) => ({
        type: row.type.value,
        fieldDataType: row.fieldType.value,
        textId: row.textId.value,
        answer: row.answer.value || '',
        result: 'NA',
      }));
    return reportData;
  }

  private swapCallLogViewCompleted(): void {
    this.callLogOrigin = 'Report';
    this.title = 'call_log.titles.call_log_report_view';
    this.callLogService.setFormData(this.formData);
    if (this.callType === 'Inbound') this.disableClientFromCallCampaigns();
    this.getReminders();
    this.buildReportData();
  }

  private getReminders() {
    if (!this.accountId) return;
    const accountNum = Number(this.accountId);
    const query = `$filter=entityType eq 'Account' and entityId eq ${accountNum} and ActivityType eq 'Task' and TaskActivity/ClosedDate eq null&$expand=taskActivity($expand=queue($select=name);$select=Id,QueueId,StartDate,AssignedTo,Priority)&$select=Id,ActivityCategory,ActivityText,ActivityType,EntityId,EntityType,ScopeId`;
    this.reminders$ = this.workloadApi
      .getActivityDetailsV2(query)
      .pipe(shareReplay(), takeUntil(this.destroy$));
  }
}
